import { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Wifi, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { pingStation, formatUptime } from '@/lib/api/demoStations';

interface PingButtonProps {
  stationId: string;
  createdBy?: string;
  variant?: 'icon' | 'button';
  disabled?: boolean;
}

const FLASH_MS = 2_500;

export function PingButton({ stationId, createdBy, variant = 'icon', disabled }: PingButtonProps) {
  const [flash, setFlash] = useState<'success' | 'error' | null>(null);

  const mutation = useMutation({
    mutationFn: () => pingStation(stationId, createdBy),
    onSuccess: ({ hostname, uptime }) => {
      toast.success(`Pong from ${hostname} • uptime ${formatUptime(uptime)}`);
      setFlash('success');
    },
    onError: (err: Error) => {
      toast.error(`Ping failed: ${err.message}`);
      setFlash('error');
    },
  });

  useEffect(() => {
    if (!flash) return;
    const t = setTimeout(() => { setFlash(null); mutation.reset(); }, FLASH_MS);
    return () => clearTimeout(t);
  }, [flash, mutation]);

  const state: 'idle' | 'pending' | 'success' | 'error' =
    mutation.isPending ? 'pending' : (flash ?? 'idle');

  const title =
    state === 'pending' ? 'Pinging…' :
    state === 'success' && mutation.data ? `Pong from ${mutation.data.hostname} • uptime ${formatUptime(mutation.data.uptime)}` :
    state === 'error' ? `Ping failed: ${mutation.error?.message ?? 'unknown'}` :
    'Ping';

  const iconSize = variant === 'icon' ? 'h-3 w-3' : 'h-3.5 w-3.5';
  const iconCls = cn(iconSize, variant === 'button' && 'mr-1.5');

  const icon =
    state === 'pending' ? <Loader2 className={cn(iconCls, 'animate-spin')} /> :
    state === 'success' ? <CheckCircle2 className={cn(iconCls, 'text-green-600')} /> :
    state === 'error'   ? <AlertCircle  className={cn(iconCls, 'text-red-600')} /> :
                          <Wifi         className={iconCls} />;

  if (variant === 'icon') {
    return (
      <Button size="icon" variant="ghost" className="h-6 w-6"
        onClick={() => mutation.mutate()}
        disabled={disabled || mutation.isPending}
        title={title}>
        {icon}
      </Button>
    );
  }

  return (
    <Button size="sm" variant="outline"
      onClick={() => mutation.mutate()}
      disabled={disabled || mutation.isPending}
      title={title}>
      {icon}
      {state === 'pending' ? 'Pinging…' : 'Ping'}
    </Button>
  );
}
